import type { NavigateFunction } from 'react-router-dom';

export interface PushNotificationPayload {
  route?: string;
  type?: string;
  entityId?: string;
  societyId?: string;
  tenantId?: string;
  title?: string;
  body?: string;
  [key: string]: any;
}

const STORAGE_KEY = 'VYORA_PENDING_DEEP_LINK';

/**
 * Allowed route prefixes to prevent arbitrary open redirects
 */
const ALLOWED_ROUTE_PREFIXES = [
  '/finance',
  '/corpus-fund',
  '/complaints',
  '/services',
  '/visitors',
  '/notices',
  '/meetings',
  '/events',
  '/amenities',
  '/elections',
  '/polls',
  '/statements',
  '/forum',
  '/invitations',
  '/water-tanker',
  '/emergencies',
  '/emergency',
  '/support',
  '/dashboard',
  '/m/home',
  '/m/',
  '/super-admin',
  '/profile',
];

/**
 * Resolves a push notification payload to a safe, allowlisted internal application route.
 */
export function resolveNotificationRoute(payload: PushNotificationPayload | null | undefined): string {
  if (!payload) return '/dashboard';

  // 1. Direct explicit route if valid and allowlisted
  if (payload.route && typeof payload.route === 'string' && payload.route.startsWith('/')) {
    if (payload.route === '/m/home') return '/dashboard';
    const isAllowed = ALLOWED_ROUTE_PREFIXES.some((prefix) => payload.route!.startsWith(prefix));
    if (isAllowed) {
      return payload.route;
    }
  }

  const type = String(payload.type || '').toUpperCase();
  const entityId = payload.entityId ? String(payload.entityId) : '';

  // 2. Mapping by notification event type and entityId
  switch (type) {
    case 'NOTICE':
    case 'NOTICE_PUBLISHED':
    case 'ANNOUNCEMENT':
      return entityId ? `/notices/${entityId}` : '/notices';

    case 'MEETING':
    case 'MEETING_SCHEDULED':
    case 'MEETING_RESCHEDULED':
      return entityId ? `/meetings/${entityId}` : '/meetings';

    case 'AMENITY':
    case 'AMENITY_UPDATED':
      return entityId ? `/amenities/${entityId}` : '/amenities';

    case 'AMENITY_BOOKING':
    case 'BOOKING_REQUESTED':
    case 'BOOKING_APPROVED':
    case 'BOOKING_REJECTED':
    case 'BOOKING_CONFIRMED':
    case 'BOOKING_CANCELLED':
      return entityId ? `/amenities/my-bookings?bookingId=${entityId}` : '/amenities/my-bookings';

    case 'ELECTION':
    case 'ELECTION_OPENED':
    case 'VOTING_OPEN':
    case 'ELECTION_RESULTS':
      return entityId ? `/elections/${entityId}` : '/elections';

    case 'POLL':
    case 'POLL_OPENED':
    case 'POLL_CLOSED':
    case 'RESIDENT_POLL':
      return entityId ? `/polls/${entityId}` : '/polls';

    case 'COLLECTION':
      return entityId ? `/finance/collections/${entityId}` : '/finance/collections';

    case 'PAYMENT':
    case 'PAYMENT_DUE':
    case 'PAYMENT_RECEIVED':
    case 'PAYMENT_RECEIPT':
      return entityId ? `/finance/statements?receiptId=${entityId}` : '/finance/statements';

    case 'DUES_STATEMENT':
    case 'ANNUAL_STATEMENT':
    case 'STATEMENT':
      return '/finance/statements';

    case 'CORPUS_FUND':
    case 'CORPUS_DUE':
    case 'CORPUS_FUND_CONTRIBUTION':
      return entityId ? `/corpus-fund/${entityId}` : '/corpus-fund';

    case 'EXPENSE':
    case 'SPECIAL_EXPENSE':
      return entityId ? `/finance/expenses/${entityId}` : '/finance/expenses';

    case 'COMPLAINT':
    case 'COMPLAINT_UPDATE':
      return entityId ? `/complaints/${entityId}` : '/complaints';

    case 'EVENT':
    case 'FACILITY':
      return entityId ? `/events/${entityId}` : '/events';

    case 'FORUM':
    case 'FORUM_POST':
    case 'THREAD':
      return entityId ? `/forum/${entityId}` : '/forum';

    case 'INVITATION':
      return '/invitations';

    case 'VISITOR':
    case 'VISITOR_ARRIVED':
    case 'VISITOR_ENTRY':
    case 'VISITOR_PRE_APPROVAL':
    case 'GATE_PASS':
      return entityId ? `/visitors/${entityId}` : '/visitors';

    case 'WATER_TANKER':
      return entityId ? `/water-tanker/${entityId}` : '/water-tanker';

    case 'SUPPORT_TICKET':
      return entityId ? `/support/tickets/${entityId}` : '/support/tickets';

    case 'EMERGENCY_ALERT':
    case 'EMERGENCY_RESOLVED':
    case 'EMERGENCY':
      return entityId ? `/emergencies/${entityId}` : '/emergencies';

    case 'GENERAL':
    default:
      return '/dashboard';
  }
}

/**
 * Stores a pending deep link target for post-login redirection.
 */
export function setPendingDeepLink(route: string): void {
  try {
    if (typeof window !== 'undefined') {
      window.sessionStorage?.setItem(STORAGE_KEY, route);
      window.localStorage?.setItem(STORAGE_KEY, route);
    }
  } catch (err) {
    console.warn('[NotificationRouter] Failed to persist pending deep link', err);
  }
}

/**
 * Retrieves the currently stored pending deep link.
 */
export function getPendingDeepLink(): string | null {
  try {
    if (typeof window !== 'undefined') {
      return (
        window.sessionStorage?.getItem(STORAGE_KEY) ||
        window.localStorage?.getItem(STORAGE_KEY) ||
        null
      );
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Clears the pending deep link from storage.
 */
export function clearPendingDeepLink(): void {
  try {
    if (typeof window !== 'undefined') {
      window.sessionStorage?.removeItem(STORAGE_KEY);
      window.localStorage?.removeItem(STORAGE_KEY);
    }
  } catch (err) {
    console.warn('[NotificationRouter] Failed to clear pending deep link', err);
  }
}

/**
 * Handles incoming push notification interaction with authentication check.
 * If authenticated: navigates straight to target route.
 * If unauthenticated: records pending target and navigates to /login.
 */
export function handleNotificationNavigation(
  payload: PushNotificationPayload,
  navigate: NavigateFunction,
  isAuthenticated: boolean,
): void {
  const targetRoute = resolveNotificationRoute(payload);

  if (isAuthenticated) {
    navigate(targetRoute);
  } else {
    setPendingDeepLink(targetRoute);
    navigate('/login', {
      state: {
        fromNotification: true,
        pendingDeepLink: targetRoute,
      },
    });
  }
}

/**
 * Checks for a pending deep link after login/biometric unlock.
 * If present, navigates to the stored route and clears the storage.
 * Returns true if a pending link was consumed, false otherwise.
 */
export function consumePendingDeepLink(navigate: NavigateFunction): boolean {
  const pendingRoute = getPendingDeepLink();
  if (pendingRoute && pendingRoute.startsWith('/')) {
    clearPendingDeepLink();
    const isAllowed = ALLOWED_ROUTE_PREFIXES.some((prefix) => pendingRoute.startsWith(prefix));
    if (isAllowed) {
      navigate(pendingRoute, { replace: true });
      return true;
    }
  }
  return false;
}
